import {List, Map} from 'immutable';
import {Expressions} from '@packages/systems/wfdl';
import {
  EHole,
  ERecord,
  EUnion,
  EText,
  type Expression,
} from '@packages/systems/wfdl-ast';
import {type CollectionRecordType} from '@packages/systems/dynamo/records/CollectionRecord';
import {NON_CONFIGURABLE_FIELD_SLUGS} from '@packages/systems/dynamo/utils/CollectionFieldUtils';
import type {FieldType as Field} from '@packages/systems/dynamo/records/Field';

const None = EUnion({
  tag: 'None',
  value: ERecord({}),
});

const Some = (value: Expression) =>
  EUnion({
    tag: 'Some',
    value,
  });

const False = EUnion({tag: 'False', value: ERecord({})});
const emptyText = EText('');

const supportedFieldTypes = {
  PlainText: true,
  Number: true,
  Bool: true,
  Email: true,
  Phone: true,
  Link: true,
  Video: true,
  Color: true,
  Option: true,
} as const;

export const isSupportedField = ({slug, type}: Field) =>
  !NON_CONFIGURABLE_FIELD_SLUGS.hasOwnProperty(slug) &&
  supportedFieldTypes.hasOwnProperty(type);

export const isSupportedCustomField = (field: Field) =>
  field.slug !== 'name' && field.slug !== 'slug' && isSupportedField(field);

const collectFieldBySlug = (acc: Record<any, any>, field: any) => {
  acc[field.slug] = field;
  return acc;
};
const bySlug = (fields: List<any>) =>
  fields.reduce<Record<string, any>>(collectFieldBySlug, {});

export const supportedFieldsBySlug = (collection: CollectionRecordType) =>
  bySlug(collection.fields.filter(isSupportedCustomField));

export const adjustFieldValue = (
  oldField: Field,
  newField: Field,
  value: Expression
): Expression => {
  if (oldField.type === newField.type) {
    const type = newField.type;
    if (oldField.required !== newField.required) {
      if (type === 'PlainText' || type === 'Phone') {
        if (newField.required && Expressions.getText(value) === '')
          return EHole();
        if (!newField.required && Expressions.isHole(value)) return EText('');
        return value;
      }
      if (
        type === 'Number' ||
        type === 'Email' ||
        type === 'Link' ||
        type === 'Video' ||
        type === 'Color'
      ) {
        if (newField.required) {
          const union = Expressions.getUnion(value);
          if (!union) return value;
          if (union.tag === 'None') return EHole();
          if (union.tag === 'Some') return union.value;
          return value;
        }
        if (Expressions.isHole(value)) return None;
        return Some(value);
      }
    }
    if (type === 'Option') {
      const union = Expressions.getUnion(value);
      if (!union) return value;
      const options = Map(newField.validations).get('options', List());
      if (!options.includes(union.tag)) return EHole();
    }
    return value;
  }
  return initializeField(newField);
};

/**
 * Utils for initializing fields, which takes the `required` flag into account
 * FixMeAny needed due to `Missing type annotation for `r`` Flow error
 */
export const initializeField = (field: Field): Expression => {
  const type = field.type;

  if (type === 'PlainText') return field.required ? EHole() : emptyText;
  if (type === 'Number') return field.required ? EHole() : None;
  if (type === 'Bool') return False;
  if (type === 'Email') return field.required ? EHole() : None;
  if (type === 'Phone') return field.required ? EHole() : emptyText;
  if (type === 'Link') return field.required ? EHole() : None;
  if (type === 'Video') return field.required ? EHole() : None;
  if (type === 'Color') return field.required ? EHole() : None;
  if (type === 'Option') return EHole();

  return EHole();
};

/** Get field from a collection by its slug. */
export const getFieldBySlug = ({
  fieldSlug,
  collection,
}: {
  fieldSlug: string;
  collection: CollectionRecordType;
}) => {
  return collection.fields?.find((f: any) => f.slug === fieldSlug);
};

/** List of fields we currently consider to be basic, in the context of updating a CMS item */
export const BASIC_FIELDS = ['name', 'slug'] as const;

/** From a list of field slugs, returns the slugs that belong to custom fields */
export const getCustomSlugs = (slugs: Array<string>): Array<string> =>
  // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"name" | "slug"'.
  slugs.filter((slug) => !BASIC_FIELDS.includes(slug));
/** From a list of field slugs, returns the slugs that are considered to be basic field */
export const getBasicSlugs = (slugs: Array<string>): Array<string> =>
  // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"name" | "slug"'.
  slugs.filter((slug) => BASIC_FIELDS.includes(slug));
